import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Save, Eye, ArrowLeft, Plus, Trash2, GripVertical, Settings, Palette,
  ChevronDown, ChevronUp, EyeOff, ExternalLink, Package, Layers, BarChart3
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import ImageUploader from '../../components/ImageUploader';

const SECTION_NAMES = {
  hero: 'Hero Banner',
  about: 'About Section',
  features: 'Features Grid',
  products: 'Products Display',
  gallery: 'Image Gallery',
  testimonials: 'Testimonials',
  team: 'Team Members',
  pricing: 'Pricing Plans',
  contact: 'Contact Form',
  faq: 'FAQ Section',
  cta: 'Call to Action',
  text: 'Text Block',
  video: 'Video Embed',
  stats: 'Statistics',
  clients: 'Client Logos',
  blog: 'Blog Posts',
  custom_html: 'Custom HTML'
};

const MyShopEditor = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('sections');
  const [expandedSections, setExpandedSections] = useState({});
  const [showAddSection, setShowAddSection] = useState(false);

  useEffect(() => {
    fetchShop();
    fetchLimits();
  }, [shopId]);

  const fetchShop = async () => {
    try {
      const res = await api.get(`/shops/my-shops/${shopId}`);
      setShop(res.data);
    } catch (error) {
      toast.error('Failed to load shop');
      navigate('/my-shops');
    } finally {
      setLoading(false);
    }
  };

  const fetchLimits = async () => {
    try {
      const res = await api.get('/shops/my-limits');
      setLimits(res.data);
    } catch (error) {
      console.error('Failed to fetch limits:', error);
    }
  };

  const saveShop = async () => {
    setSaving(true);
    try {
      await api.put(`/shops/my-shops/${shopId}`, shop);
      toast.success('Shop saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save shop');
    } finally {
      setSaving(false);
    }
  };

  const updateShop = (updates) => {
    setShop(prev => ({ ...prev, ...updates }));
  };

  const togglePublish = async () => {
    const newStatus = !shop.is_published;
    try {
      await api.put(`/shops/my-shops/${shopId}`, { is_published: newStatus });
      setShop(prev => ({ ...prev, is_published: newStatus }));
      toast.success(newStatus ? 'Shop published!' : 'Shop unpublished');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const addSection = (type) => {
    const newSection = {
      id: `section_${Date.now()}`,
      type,
      order: (shop.sections?.length || 0) + 1,
      is_visible: true,
      content: {}
    };
    
    setShop(prev => ({
      ...prev,
      sections: [...(prev.sections || []), newSection]
    }));
    setShowAddSection(false);
    setExpandedSections(prev => ({ ...prev, [newSection.id]: true }));
  };

  const updateSection = (sectionId, updates) => {
    setShop(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, ...updates } : s
      )
    }));
  };

  const deleteSection = (sectionId) => {
    if (!confirm('Delete this section?')) return;
    setShop(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId)
    }));
  };

  const moveSection = (index, direction) => {
    const sections = [...shop.sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
    sections.forEach((s, i) => s.order = i + 1);
    setShop(prev => ({ ...prev, sections }));
  };

  const canAddSection = !limits?.max_sections_per_shop || 
    limits.max_sections_per_shop === -1 || 
    (shop?.sections?.length || 0) < limits.max_sections_per_shop;

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!shop) return null;

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="my-shop-editor">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/my-shops')}
              className="p-2 hover:bg-white/10 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">{shop.name}</h1>
              <p className="text-sm text-muted-foreground">/{shop.slug}</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Link
              to={`/my-shops/${shopId}/analytics`}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-500 rounded-lg hover:bg-purple-500/30"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </Link>
            {shop.is_published && (
              <a
                href={`/shop/${shop.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10"
              >
                <Eye className="w-4 h-4" />
                Preview
              </a>
            )}
            <button
              onClick={togglePublish}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                shop.is_published 
                  ? 'bg-green-500/20 text-green-500' 
                  : 'bg-yellow-500/20 text-yellow-500'
              }`}
            >
              {shop.is_published ? 'Published' : 'Draft'}
            </button>
            <button
              onClick={saveShop}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-2">
          {['sections', 'products', 'design', 'settings'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-t-lg capitalize transition-colors ${
                activeTab === tab 
                  ? 'bg-primary/20 text-primary border-b-2 border-primary' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Sections Tab */}
        {activeTab === 'sections' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">
                {shop.sections?.length || 0} sections
                {limits?.max_sections_per_shop > 0 && ` / ${limits.max_sections_per_shop} max`}
              </p>
              {canAddSection && (
                <button
                  onClick={() => setShowAddSection(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary/20 text-primary rounded-lg"
                >
                  <Plus className="w-4 h-4" />
                  Add Section
                </button>
              )}
            </div>

            {/* Sections List */}
            <div className="space-y-3">
              {shop.sections?.sort((a, b) => a.order - b.order).map((section, index) => {
                const isExpanded = expandedSections[section.id];
                return (
                  <div
                    key={section.id}
                    className="glass-card overflow-hidden"
                  >
                    <div className="flex items-center gap-3 p-4">
                      <GripVertical className="w-5 h-5 text-muted-foreground cursor-move" />
                      
                      <div className="flex-1">
                        <p className="font-medium">{SECTION_NAMES[section.type] || section.type}</p>
                        <p className="text-xs text-muted-foreground">Type: {section.type}</p>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => moveSection(index, 'up')}
                          disabled={index === 0}
                          className="p-1 hover:bg-white/10 rounded disabled:opacity-30"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => moveSection(index, 'down')}
                          disabled={index === shop.sections.length - 1}
                          className="p-1 hover:bg-white/10 rounded disabled:opacity-30"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => updateSection(section.id, { is_visible: !section.is_visible })}
                          className={`p-2 rounded ${section.is_visible ? 'text-green-500' : 'text-muted-foreground'}`}
                        >
                          {section.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => deleteSection(section.id)}
                          className="p-2 text-red-500 hover:bg-red-500/20 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !isExpanded }))}
                          className="p-2 hover:bg-white/10 rounded"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="p-4 border-t border-white/10 space-y-4">
                        <SectionEditor
                          section={section}
                          shopId={shopId}
                          onUpdate={(updates) => updateSection(section.id, updates)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Design Tab */}
        {activeTab === 'design' && (
          <div className="glass-card p-6 space-y-6">
            <h3 className="text-lg font-semibold">Colors</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Primary</label>
                <input
                  type="color"
                  value={shop.colors?.primary || '#D4AF37'}
                  onChange={(e) => updateShop({ colors: { ...shop.colors, primary: e.target.value } })}
                  className="w-full h-12 rounded-lg cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Secondary</label>
                <input
                  type="color"
                  value={shop.colors?.secondary || '#1a1a2e'}
                  onChange={(e) => updateShop({ colors: { ...shop.colors, secondary: e.target.value } })}
                  className="w-full h-12 rounded-lg cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Accent</label>
                <input
                  type="color"
                  value={shop.colors?.accent || '#6366f1'}
                  onChange={(e) => updateShop({ colors: { ...shop.colors, accent: e.target.value } })}
                  className="w-full h-12 rounded-lg cursor-pointer"
                />
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="glass-card p-6 space-y-6">
            <h3 className="text-lg font-semibold">Shop Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Shop Name</label>
                <input
                  type="text"
                  value={shop.name || ''}
                  onChange={(e) => updateShop({ name: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={shop.description || ''}
                  onChange={(e) => updateShop({ description: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <ProductsManager shopId={shopId} limits={limits} />
        )}

        {/* Add Section Modal */}
        {showAddSection && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Add Section</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(SECTION_NAMES).map(([type, name]) => (
                  <button
                    key={type}
                    onClick={() => addSection(type)}
                    className="p-4 bg-white/5 hover:bg-white/10 rounded-lg text-left"
                  >
                    <p className="font-medium">{name}</p>
                    <p className="text-xs text-muted-foreground">{type}</p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowAddSection(false)}
                className="mt-4 w-full px-4 py-2 bg-white/5 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple Section Editor
const SectionEditor = ({ section, shopId, onUpdate }) => {
  const content = section.content || {};

  const updateContent = (key, value) => {
    onUpdate({ content: { ...content, [key]: value } });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Title</label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => updateContent('title', e.target.value)}
          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
        />
      </div>
      {(section.type === 'about' || section.type === 'text' || section.type === 'hero') && (
        <div>
          <label className="block text-sm font-medium mb-2">
            {section.type === 'hero' ? 'Subtitle' : 'Content'}
          </label>
          <textarea
            value={content.subtitle || content.text || ''}
            onChange={(e) => updateContent(section.type === 'hero' ? 'subtitle' : 'text', e.target.value)}
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            rows={3}
          />
        </div>
      )}
      {section.type === 'hero' && (
        <ImageUploader
          shopId={shopId}
          category="hero"
          currentImage={content.background_image}
          onImageUploaded={(url) => updateContent('background_image', url)}
          onImageRemoved={() => updateContent('background_image', '')}
          label="Background Image"
        />
      )}
    </div>
  );
};

// Products Manager Component
const ProductsManager = ({ shopId, limits }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    sale_price: '',
    stock: -1,
    images: [],
    category: '',
    is_digital: true,
    download_url: ''
  });

  useEffect(() => {
    fetchProducts();
  }, [shopId]);

  const fetchProducts = async () => {
    try {
      const res = await api.get(`/shops/my-shops/${shopId}/products`);
      setProducts(res.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price) {
      toast.error('Name and price are required');
      return;
    }

    try {
      await api.post(`/shops/my-shops/${shopId}/products`, {
        ...newProduct,
        price: parseFloat(newProduct.price),
        sale_price: newProduct.sale_price ? parseFloat(newProduct.sale_price) : null
      });
      toast.success('Product added!');
      setShowAddModal(false);
      setNewProduct({
        name: '',
        description: '',
        price: '',
        sale_price: '',
        stock: -1,
        images: [],
        category: '',
        is_digital: true,
        download_url: ''
      });
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add product');
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct.name || !editingProduct.price) {
      toast.error('Name and price are required');
      return;
    }

    try {
      await api.put(`/shops/my-shops/${shopId}/products/${editingProduct.id}`, {
        ...editingProduct,
        price: parseFloat(editingProduct.price),
        sale_price: editingProduct.sale_price ? parseFloat(editingProduct.sale_price) : null
      });
      toast.success('Product updated!');
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Delete this product?')) return;

    try {
      await api.delete(`/shops/my-shops/${shopId}/products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const canAddProduct = !limits?.max_products_per_shop || 
    limits.max_products_per_shop === -1 || 
    products.length < limits.max_products_per_shop;

  if (loading) {
    return (
      <div className="glass-card p-8 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-muted-foreground">
          {products.length} products
          {limits?.max_products_per_shop > 0 && ` / ${limits.max_products_per_shop} max`}
        </p>
        {canAddProduct && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary/20 text-primary rounded-lg"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </button>
        )}
      </div>

      {/* Products Grid */}
      {products.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(product => (
            <div key={product.id} className="glass-card p-4">
              {product.images?.[0] && (
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-32 object-cover rounded-lg mb-3"
                />
              )}
              <h4 className="font-semibold truncate">{product.name}</h4>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{product.description}</p>
              <div className="flex items-center gap-2 mb-3">
                {product.sale_price ? (
                  <>
                    <span className="text-lg font-bold text-primary">₹{product.sale_price}</span>
                    <span className="text-sm line-through text-muted-foreground">₹{product.price}</span>
                  </>
                ) : (
                  <span className="text-lg font-bold text-primary">₹{product.price}</span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingProduct(product)}
                  className="flex-1 px-3 py-2 bg-white/5 rounded-lg text-sm hover:bg-white/10"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteProduct(product.id)}
                  className="px-3 py-2 bg-red-500/20 text-red-500 rounded-lg text-sm hover:bg-red-500/30"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="glass-card p-8 text-center">
          <Package className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No products yet</p>
          {canAddProduct && (
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 btn-primary"
            >
              Add Your First Product
            </button>
          )}
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {(showAddModal || editingProduct) && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="glass-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingProduct ? 'Edit Product' : 'Add Product'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Product Name *</label>
                <input
                  type="text"
                  value={editingProduct ? editingProduct.name : newProduct.name}
                  onChange={(e) => editingProduct 
                    ? setEditingProduct({...editingProduct, name: e.target.value})
                    : setNewProduct({...newProduct, name: e.target.value})
                  }
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  placeholder="Product name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={editingProduct ? editingProduct.description : newProduct.description}
                  onChange={(e) => editingProduct
                    ? setEditingProduct({...editingProduct, description: e.target.value})
                    : setNewProduct({...newProduct, description: e.target.value})
                  }
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  rows={3}
                  placeholder="Product description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Price (₹) *</label>
                  <input
                    type="number"
                    value={editingProduct ? editingProduct.price : newProduct.price}
                    onChange={(e) => editingProduct
                      ? setEditingProduct({...editingProduct, price: e.target.value})
                      : setNewProduct({...newProduct, price: e.target.value})
                    }
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Sale Price (₹)</label>
                  <input
                    type="number"
                    value={editingProduct ? editingProduct.sale_price || '' : newProduct.sale_price}
                    onChange={(e) => editingProduct
                      ? setEditingProduct({...editingProduct, sale_price: e.target.value})
                      : setNewProduct({...newProduct, sale_price: e.target.value})
                    }
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <input
                  type="text"
                  value={editingProduct ? editingProduct.category || '' : newProduct.category}
                  onChange={(e) => editingProduct
                    ? setEditingProduct({...editingProduct, category: e.target.value})
                    : setNewProduct({...newProduct, category: e.target.value})
                  }
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  placeholder="e.g., Electronics, Clothing"
                />
              </div>

              {/* Product Image URL */}
              <div>
                <label className="block text-sm font-medium mb-2">Product Image URL</label>
                <input
                  type="text"
                  value={editingProduct ? (editingProduct.images?.[0] || '') : (newProduct.images?.[0] || '')}
                  onChange={(e) => {
                    const imageUrl = e.target.value;
                    if (editingProduct) {
                      setEditingProduct({...editingProduct, images: imageUrl ? [imageUrl] : []});
                    } else {
                      setNewProduct({...newProduct, images: imageUrl ? [imageUrl] : []});
                    }
                  }}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  placeholder="https://example.com/image.jpg"
                />
                <p className="text-xs text-muted-foreground mt-1">Paste an image URL from the web</p>
                {(editingProduct?.images?.[0] || newProduct.images?.[0]) && (
                  <img 
                    src={editingProduct?.images?.[0] || newProduct.images?.[0]} 
                    alt="Preview" 
                    className="mt-2 w-20 h-20 object-cover rounded-lg border border-white/10"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_digital"
                  checked={editingProduct ? editingProduct.is_digital : newProduct.is_digital}
                  onChange={(e) => editingProduct
                    ? setEditingProduct({...editingProduct, is_digital: e.target.checked})
                    : setNewProduct({...newProduct, is_digital: e.target.checked})
                  }
                  className="w-4 h-4"
                />
                <label htmlFor="is_digital" className="text-sm">Digital Product (downloadable)</label>
              </div>

              {(editingProduct?.is_digital || newProduct.is_digital) && (
                <div>
                  <label className="block text-sm font-medium mb-2">Download URL</label>
                  <input
                    type="text"
                    value={editingProduct ? editingProduct.download_url || '' : newProduct.download_url}
                    onChange={(e) => editingProduct
                      ? setEditingProduct({...editingProduct, download_url: e.target.value})
                      : setNewProduct({...newProduct, download_url: e.target.value})
                    }
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="https://..."
                  />
                </div>
              )}

              <ImageUploader
                shopId={shopId}
                category="product"
                currentImage={editingProduct ? editingProduct.images?.[0] : newProduct.images?.[0]}
                onImageUploaded={(url) => editingProduct
                  ? setEditingProduct({...editingProduct, images: [url]})
                  : setNewProduct({...newProduct, images: [url]})
                }
                onImageRemoved={() => editingProduct
                  ? setEditingProduct({...editingProduct, images: []})
                  : setNewProduct({...newProduct, images: []})
                }
                label="Product Image"
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={editingProduct ? handleUpdateProduct : handleAddProduct}
                className="flex-1 btn-primary"
              >
                {editingProduct ? 'Update Product' : 'Add Product'}
              </button>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingProduct(null);
                }}
                className="flex-1 px-4 py-2 bg-white/5 rounded-full"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyShopEditor;
